import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Paper, Divider, Button, Checkbox, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { setSelectedEmails, fetchAllMails, selectAll, selectNewsletterSubscribers, clearSelection, sendBatchMail } from '../store/slices/mailSlice';
import { fetchAllStartups } from '../store/slices/adminSlice';
import { ApiHelper } from '../utils/apiHelper';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';

const MailManagementPage = () => {
  const INVESTMENT_TYPES = {
    ALL: "ALL",
    REGULAR: "REGULAR",
    PRE_FUND: "PRE_FUND"
  };

  const dispatch = useDispatch();
  const { emails, selectedEmails, loading, error } = useSelector((state) => state.mail);
  const { allStartups, loading: startupLoading } = useSelector((state) => state.admin);

  const [mailSubject, setMailSubject] = useState('');
  const [mailContent, setMailContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedInvestment, setSelectedInvestment] = useState(INVESTMENT_TYPES.ALL);
  const [investmentEmails, setInvestmentEmails] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  const [allUserEmails, setAllUserEmails] = useState([]);

  // Sayfa yüklendiğinde tüm kullanıcıların emaillerini getir
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await ApiHelper.get('/admin/all-users');
        if (response.data.code === '00000') {
          const users = response.data.response?.userList || [];
          const emails = users.map(user => ({
            email: user.email,
            newsletterSubscription: user.newsletterSubscription || false,
            emailVerification: user.emailVerification || false,
            phoneVerification: user.phoneVerification || false,
            eDevletApproved: user.eDevletApproved || false,
            startupId: null
          }));
          setAllUserEmails(emails);
        }
      } catch (error) {
        console.error('Error fetching all users:', error);
        toast.error('Kullanıcı bilgileri alınırken bir hata oluştu');
      }
    };

    fetchAllUsers();
  }, []);

  useEffect(() => {
    dispatch(fetchAllMails());
    dispatch(fetchAllStartups({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Proje veya yatırım tipi değiştiğinde yatırımcıları getir
  useEffect(() => {
    const fetchInvestors = async () => {
      if (!selectedProject) {
        setInvestmentEmails([]);
        return;
      }

      setLoadingInvestments(true);
      try {
        const response = await ApiHelper.get(`/admin/startup-investments/${selectedProject}/${selectedInvestment}`);
        
        if (response.data.code === '00000') {
          const emails = response.data.emails || [];
          // Convert email list to the required format
          const formattedEmails = emails.map(email => ({
            email: email,
            startupId: selectedProject
          }));
          setInvestmentEmails(formattedEmails);
        }
      } catch (error) {
        console.error('Error fetching investors:', error);
        toast.error('Yatırımcı bilgileri alınırken bir hata oluştu');
        setInvestmentEmails([]);
      } finally {
        setLoadingInvestments(false);
      }
    };

    fetchInvestors();
  }, [selectedProject, selectedInvestment]);

  // Filtrelenmiş emailleri hesaplayan değişken - null check ekle
  const filteredEmails = (selectedProject ? investmentEmails : allUserEmails || []).filter(item => 
    item && item.email && item.email.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  // /admin/user-mails endpoint'inden gelen mailleri filtrele
  const filteredUserMails = emails?.filter(mail => 
    mail && mail.email && mail.email.toLowerCase().includes((searchTerm || '').toLowerCase())
  ) || [];

  // Debug için mail verilerini logla
  console.log('Redux emails state:', emails);
  console.log('Filtered user mails:', filteredUserMails);

  const handleSelectAll = () => {
    // Hem sistem maillerinden hem de kullanıcı emaillerinden tümünü seç
    const allEmails = [
      ...(filteredUserMails.map(mail => mail.email) || []),
      ...(filteredEmails.map(item => item.email) || [])
    ];
    
    // Eğer tüm emailler zaten seçiliyse, hepsini kaldır
    const allSelected = allEmails.every(email => selectedEmails.includes(email));
    if (allSelected) {
      dispatch(setSelectedEmails([]));
    } else {
      dispatch(setSelectedEmails(allEmails));
    }
  };

  const handleSelectNewsletterSubscribers = () => {
    // Sadece mail izni olan kullanıcıları seç
    const newsletterEmails = emails
      ?.filter(item => item && item.newsletterSubscription === true)
      .map(item => item.email) || [];
    
    console.log('Newsletter subscribers:', newsletterEmails); // Debug için
    
    // Eğer newsletter emailleri zaten seçiliyse, hepsini kaldır
    const allNewsletterSelected = newsletterEmails.every(email => selectedEmails.includes(email));
    if (allNewsletterSelected) {
      dispatch(setSelectedEmails([]));
    } else {
      dispatch(setSelectedEmails(newsletterEmails));
    }
  };

  const handleSelectUnverifiedUsers = () => {
    // Doğrulama yapmayan kullanıcıları seç
    const unverifiedEmails = allUserEmails
      ?.filter(user => {
        // Email, telefon veya E-Devlet doğrulaması yapılmamış kullanıcıları filtrele
        return !user.emailVerification || !user.phoneVerification || !user.eDevletApproved;
      })
      .map(user => user.email) || [];
    
    console.log('Unverified users:', unverifiedEmails); // Debug için
    
    // Eğer doğrulama yapmayan emailler zaten seçiliyse, hepsini kaldır
    const allUnverifiedSelected = unverifiedEmails.every(email => selectedEmails.includes(email));
    if (allUnverifiedSelected) {
      dispatch(setSelectedEmails([]));
    } else {
      dispatch(setSelectedEmails(unverifiedEmails));
    }
  };

  const handleEmailSelect = (email) => {
    const newSelection = selectedEmails.includes(email)
      ? selectedEmails.filter(e => e !== email)
      : [...selectedEmails, email];
    dispatch(setSelectedEmails(newSelection));
  };

  const handleSendMail = () => {
    // Mail gönderme işlemi burada yapılacak
    try {
        console.log("Mail gönderiliyor:", mailSubject, mailContent, selectedEmails);
        dispatch(sendBatchMail(mailSubject, mailContent, selectedEmails));

        toast.success('Mail başarıyla gönderildi!');
    } catch (error) {
        console.error("Mail gönderme hatası:", error.message);
        toast.error('Mail gönderme işlemi başarısız oldu!');
    }
    // Örnek API çağrısı
  }

  if (loading || startupLoading || loadingInvestments) return <div>Yükleniyor...</div>;
  if (error) return <div>Hata: {error}</div>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mail Yönetim Paneli
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ width: '40%' }}>
          <Paper sx={{ p: 2 }}>
            
            {/* Özet bilgiler */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Toplam Mail: {filteredUserMails.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seçili Mail: {selectedEmails.filter(email => 
                  filteredUserMails.some(mail => mail.email === email)
                ).length}
              </Typography>
            </Box>
            
            {/* Kullanıcı emailleri için özet bilgiler */}
            {filteredUserMails.length === 0 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Toplam Kullanıcı: {filteredEmails.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Doğrulama Yapmayan: {filteredEmails.filter(user => 
                    !user.emailVerification || !user.phoneVerification || !user.eDevletApproved
                  ).length}
                </Typography>
              </Box>
            )}
            
            <TextField
              fullWidth
              size="small"
              label="Mail Ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button 
                variant="contained" 
                onClick={handleSelectAll}
                fullWidth
              >
                Herkesi Seç
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSelectNewsletterSubscribers}
                fullWidth
              >
                Sadece Maillere İzin Verenleri Seç
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSelectUnverifiedUsers}
                fullWidth
              >
                Doğrulama Yapmayanları Seç
              </Button>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Proje Seç</InputLabel>
                <Select
                  value={selectedProject}
                  label="Proje Seç"
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <MenuItem value={null}>Seçiniz</MenuItem>
                  {allStartups?.list?.map((startup) => (
                    <MenuItem key={startup.startupId} value={startup.startupId}>
                      {startup.startupProfile?.name || 'İsimsiz Proje'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Yatırımlar</InputLabel>
                <Select
                  value={selectedInvestment}
                  label="Yatırımlar"
                  onChange={(e) => setSelectedInvestment(e.target.value)}
                >
                  <MenuItem value={INVESTMENT_TYPES.ALL}>Hepsi</MenuItem>
                  <MenuItem value={INVESTMENT_TYPES.PRE_FUND}>Ön Yatırım Yapanlar</MenuItem>
                  <MenuItem value={INVESTMENT_TYPES.REGULAR}>Yatırım Yapanlar</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Divider sx={{ my: 2 }} />
            
            {/* /admin/user-mails endpoint'inden gelen mailleri göster */}
            {filteredUserMails.map((mail) => (
              <Box 
                key={mail.email}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Checkbox
                  checked={selectedEmails.includes(mail.email)}
                  onChange={() => handleEmailSelect(mail.email)}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography>{mail.email}</Typography>
                </Box>
              </Box>
            ))}
            
            {/* Eğer sistem mailleri yoksa, kullanıcı emaillerini göster */}
            {filteredUserMails.length === 0 && filteredEmails.map((item) => (
              <Box 
                key={item.email}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Checkbox
                  checked={selectedEmails.includes(item.email)}
                  onChange={() => handleEmailSelect(item.email)}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography>{item.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.newsletterSubscription ? 'Mail izni var' : 'Mail izni yok'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.emailVerification ? '✓ Email doğrulandı' : '✗ Email doğrulanmadı'} | 
                    {item.phoneVerification ? '✓ Telefon doğrulandı' : '✗ Telefon doğrulanmadı'} | 
                    {item.eDevletApproved ? '✓ E-Devlet doğrulandı' : '✗ E-Devlet doğrulanmadı'}
                  </Typography>
                </Box>
              </Box>
            ))}
            
            {filteredUserMails.length === 0 && filteredEmails.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {emails && emails.length > 0 ? 'Arama sonucu bulunamadı' : 'Henüz mail yüklenmedi veya mail bulunamadı'}
              </Typography>
            )}
          </Paper>
        </Box>
        
        <Box sx={{ width: '60%' }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Mail İçeriği
            </Typography>
            
            <TextField
              fullWidth
              label="Mail Başlığı"
              value={mailSubject}
              onChange={(e) => setMailSubject(e.target.value)}
              margin="normal"
            />
            
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Mail İçeriği
              </Typography>
              <ReactQuill
                value={mailContent}
                onChange={setMailContent}
                style={{ height: '300px', marginBottom: '50px' }}
                theme="snow"
              />
            </Box>

            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              disabled={!mailSubject || !mailContent || selectedEmails.length === 0}
              sx={{ mt: 4 }}
              onClick={handleSendMail}
            >
              Mail Gönder ({selectedEmails.length} kişiye)
            </Button>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MailManagementPage;